import { differenceInCalendarDays, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

import { POSTPARTUM_TRACKER_DAYS } from "@/pregnancy/constants/postpartum";
import { middleOfDay } from "@/utils/datetime";

/**
 * Computes postpartum age (weeks, days, totalDays) at an arbitrary reference date.
 *
 * Birth dates use the UTC calendar date from the stored ISO string; reference dates
 * use the user's local calendar day. Both are normalized with `middleOfDay` so
 * UTC-midnight delivery timestamps do not shift a day in US timezones.
 *
 * @param birthDateISO - ISO date or datetime string of the delivery/birth date
 * @param referenceDateISO - ISO date or datetime to evaluate postpartum age at;
 *   defaults to today when omitted
 * @returns `{ weeks, days, totalDays }` when within the postpartum window,
 *   `undefined` when the reference date is before the birth or past
 *   `POSTPARTUM_TRACKER_DAYS`
 */
export const postpartumWeeksAndDaysAt = (
  birthDateISO: string,
  referenceDateISO?: string,
): { weeks: number; days: number; totalDays: number } | undefined => {
  const birthDate = middleOfDay(formatInTimeZone(parseISO(birthDateISO), "UTC", "yyyy-MM-dd"));
  const referenceDate = middleOfDay(referenceDateISO ?? new Date());

  const totalDays = differenceInCalendarDays(referenceDate!, birthDate!);

  if (totalDays < 0 || totalDays > POSTPARTUM_TRACKER_DAYS) return undefined;

  return { weeks: Math.floor(totalDays / 7), days: totalDays % 7, totalDays };
};
